import {useEffect, useEffectEvent, useRef, useState} from 'react'
import {type ReviewerModification, useModifyReviewers} from '../../../../mutations/use-modify-reviewers'
import {usePullRequestAnalytics} from '../../../../hooks/use-pull-request-analytics'
import type {ReviewerOption} from './reviewer-options'

/**
 * Owns the selection transaction. The picker is mounted only while open, so this
 * hook's lifetime *is* the open session:
 *
 * - `draftLogins` tracks the in-progress selection, seeded at mount from the
 *   currently-requested reviewers.
 * - `sessionOptionsRef` accumulates every option seen this session, keyed by
 *   login, so a reviewer picked and then filtered out of a later response is
 *   still resolvable (login → reviewerId/isTeam) when committing.
 * - On unmount (close) the add/remove delta is computed against the mount-time
 *   snapshot and sent as a single batched mutation.
 */
export function useReviewerSelectionSession({
  options,
  requestedLogins,
}: {
  options: readonly ReviewerOption[]
  requestedLogins: ReadonlySet<string>
}) {
  const {mutate: modifyReviewers} = useModifyReviewers()
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  // Baseline + initial draft are the requested set captured at mount; later prop
  // changes don't move them.
  const [snapshot] = useState<ReadonlySet<string>>(() => requestedLogins)
  const [draftLogins, setDraftLogins] = useState<ReadonlySet<string>>(() => requestedLogins)

  // Grow the accumulator as new options arrive while open.
  const sessionOptionsRef = useRef<Map<string, ReviewerOption>>(new Map())
  useEffect(() => {
    for (const option of options) sessionOptionsRef.current.set(option.login, option)
  }, [options])

  // Reconcile only the currently-visible logins so selections hidden by the
  // active filter are preserved.
  const onSelectionChange = (visibleLogins: readonly string[], nextLogins: ReadonlySet<string>) => {
    setDraftLogins(prev => {
      const updated = new Set(prev)
      for (const login of visibleLogins) {
        if (nextLogins.has(login)) updated.add(login)
        else updated.delete(login)
      }
      return updated
    })
  }

  // Compute the close-time delta against the snapshot and send it as one batched
  // mutation. As an effect event it always reads the final draft on unmount.
  const commit = useEffectEvent(() => {
    const addReviewers: ReviewerModification[] = []
    const removeReviewers: ReviewerModification[] = []
    for (const login of draftLogins) {
      if (snapshot.has(login)) continue
      const option = sessionOptionsRef.current.get(login)
      if (!option) continue
      sendPullRequestAnalyticsEvent('overview.add_reviewer', 'OVERVIEW_REVIEWER')
      addReviewers.push({reviewerId: option.reviewerId, isTeam: option.isTeam})
    }
    for (const login of snapshot) {
      if (draftLogins.has(login)) continue
      const option = sessionOptionsRef.current.get(login)
      if (!option) continue
      sendPullRequestAnalyticsEvent('overview.remove_reviewer', 'OVERVIEW_REVIEWER')
      removeReviewers.push({reviewerId: option.reviewerId, isTeam: option.isTeam})
    }
    if (addReviewers.length > 0 || removeReviewers.length > 0) {
      modifyReviewers({addReviewers, removeReviewers})
    }
  })

  // Commit on unmount (close), then drop retained options so they don't leak.
  useEffect(() => {
    const sessionOptions = sessionOptionsRef.current
    return () => {
      commit()
      sessionOptions.clear()
    }
  }, [])

  return {onSelectionChange, draftLogins}
}
